import React from "react";

import { AnimatedFocusBorder } from "./AnimatedFocusBorder";

type InputUnderlineVariant = "focus-line" | "focus-border" | "none";

interface InputUnderlineProps {
    /**
     * Which underline treatment to render.
     *
     * - `"focus-line"` — gray baseline + charcoal line that scales L→R on
     *   focus. Renders as a flex child with `mt-1 md:mt-3`, mirroring the
     *   SeekBar's layout footprint so seekable / non-seekable inputs sit at
     *   the same total height.
     * - `"focus-border"` — gray baseline pinned to the bordered input box's
     *   bottom + 4-sided animated border on focus (`<AnimatedFocusBorder>`).
     *   For landing-style inputs (`focusBorder` prop on AmountInput); does
     *   not take layout space.
     * - `"none"` — render nothing. Used when the consumer provides its own
     *   underline (e.g., `<SeekBar>` brings its own gray track).
     */
    variant: InputUnderlineVariant;
    /** Whether the parent input is currently focused. Drives the animation. */
    focused: boolean;
    /** Gate `focus-border` behind first interaction so the OUT animation
     *  doesn't play on initial mount. */
    hasInteracted?: boolean;
}

/**
 * Underline / focus chrome for `AmountInput`. Three mutually exclusive
 * variants matched to the consumer's input style.
 *
 * `"focus-line"` renders as a normal flex child (takes ~5px / 13px of layout
 * space). `"focus-border"` and `"none"` render absolutely / not at all and
 * take no layout space — caller must own `position: relative` on the parent.
 */
export function InputUnderline({
    variant,
    focused,
    hasInteracted = true
}: InputUnderlineProps): React.ReactElement | null {
    if (variant === "none") return null;

    if (variant === "focus-line") {
        return (
            <div className="mt-1 md:mt-3 relative">
                <div className="h-px bg-line-base" />
                <div
                    className="absolute top-0 left-0 right-0 h-px bg-foreground-base"
                    style={{
                        transformOrigin: "left",
                        transform: focused ? "scaleX(1)" : "scaleX(0)",
                        transition: "transform 400ms ease-out"
                    }}
                />
            </div>
        );
    }

    // focus-border: gray baseline pinned to outer's bottom + animated border
    return (
        <>
            <div className="absolute bottom-0 left-0 right-0 h-px bg-line-base" />
            {hasInteracted && <AnimatedFocusBorder focused={focused} />}
        </>
    );
}
